// frontend/src/pages/admin/ServiceCatalogPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Spin } from 'antd';
import ProductCategories from './ProductCategories';
import Products from './Products';
import Contracts from './Contracts';
import { useAuth } from '../../context/AuthContext';
import './AdminPanelTabs.css';

const { TabPane } = Tabs;

const ServiceCatalogPage = () => {
  const { 
    user, 
    loading, 
    hasPermission,
    PERMISSION_LEVELS,
    initialized 
  } = useAuth();
  const [activeTabKey, setActiveTabKey] = useState('product-categories');

  // Define tabs with their permission requirements
  const tabs = useMemo(() => [
    {
      key: 'product-categories',
      tab: 'Product Categories',
      content: <ProductCategories />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'products',
      tab: 'Enterprise Products',
      content: <Products productType="enterprise" />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'euc-products',
      tab: 'EUC Products',
      content: <Products productType="euc" />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'contracts',
      tab: 'Contracts',
      content: <Contracts />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    }
  ], [PERMISSION_LEVELS]);

  // Filter tabs based on permissions
  const accessibleTabs = useMemo(() => {
    if (!initialized || loading || !user) {
      return [];
    }
    return tabs.filter(tab => 
      hasPermission(
        tab.requiredPermission.resource,
        tab.requiredPermission.action,
        tab.requiredPermission.level
      )
    );
  }, [tabs, hasPermission, initialized, loading, user]);

  // Ensure a valid active tab is selected
  useEffect(() => {
    if (initialized && !loading && user && accessibleTabs.length > 0) {
      if (!accessibleTabs.some(tab => tab.key === activeTabKey)) {
        setActiveTabKey(accessibleTabs[0].key);
      }
    }
  }, [activeTabKey, initialized, loading, user, accessibleTabs]);

  // Loading state
  if (!initialized || loading || !user) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Spin size="large" tip="Loading Service Catalog Settings..." />
      </div>
    );
  }

  // No access state
  if (accessibleTabs.length === 0) {
    return (
      <div className="admin-page-container">
        <h1 className="page-title">Service Catalog Settings</h1>
        <p className="page-description">You do not have permission to view any service catalog tabs.</p>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <h1 className="page-title">Service Catalog Settings</h1>
      <p className="page-description">
        Manage product categories, individual products, and contracts.
      </p>

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        className="admin-panel-tabs"
      >
        {accessibleTabs.map(tab => (
          <TabPane tab={tab.tab} key={tab.key}>
            {tab.content}
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default ServiceCatalogPage;